import { MenuModel } from '../models/menu.model';

export interface TelephonyPluginLabels {
  agent: string;
  caller: string;
  businessRecord: string;
  internalPeer: string;
}

export interface QueueMappingEntry {
  name: string;
  label: string;
  priority: 'High' | 'Medium' | 'Low';
  active: boolean;
}

export interface IvrOption {
  key: string;
  label: string;
  action: 'queue' | 'menu' | 'extension' | 'hangup';
  target: string;
}

export interface IvrMenu {
  menuId: string;
  name: string;
  greeting: string;
  options: IvrOption[];
}

export interface DidRoutingEntry {
  did: string;
  label: string;
  targetType: 'queue' | 'ivr' | 'extension';
  target: string;
  active: boolean;
}

export interface DaySchedule {
  day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  open: boolean;
  openTime: string;
  closeTime: string;
}

export interface BusinessHours {
  timezone: string;
  weekdays: DaySchedule[];
  holidays: { date: string; name: string }[];
}

export interface TelephonyPluginConfig {
  pluginId: string;
  hostSystem: string;
  mountPath: string;
  labels: TelephonyPluginLabels;
  roles: {
    agent: string;
    supervisor: string;
    admin: string;
  };
  adapter: {
    type: 'grmis' | 'hmis' | 'crm' | 'webhook' | 'none';
    callerLookupUrl: string;
    wrapUpUrl: string;
    recordingWebhookUrl: string;
  };
  wrapUpOptions: {
    dispositions: string[];
    callReasons: string[];
    sources: string[];
  };
  sla: {
    targetAnswerSeconds: number;
    targetHandleSeconds: number;
    targetHoldSeconds: number;
    callbackOverdueMinutes: number;
  };
  queueMapping: QueueMappingEntry[];
  ivrMenus: IvrMenu[];
  didRouting: DidRoutingEntry[];
  businessHours: BusinessHours;
  menu: MenuModel;
  storageKeys: {
    sipExtension: string;
    sipPassword: string;
    legacySipExtension: string;
    legacySipPassword: string;
  };
}

export const TELEPHONY_PLUGIN_CONFIG: TelephonyPluginConfig = {
  pluginId: 'asterisk-contact-center',
  hostSystem: 'GRMIS',
  mountPath: '/telephony',
  labels: {
    agent: 'Agent',
    caller: 'Caller',
    businessRecord: 'Complaint',
    internalPeer: 'Agent',
  },
  roles: {
    agent: 'CSR',
    supervisor: 'SUPERVISOR',
    admin: 'ADMIN',
  },
  adapter: {
    type: 'grmis',
    callerLookupUrl: '/api/v1/telephony/calls/cti/{phone}',
    wrapUpUrl: '/api/v1/telephony/calls/log/{channelId}/wrapup',
    recordingWebhookUrl: '/api/v1/telephony/calls/recordings',
  },
  wrapUpOptions: {
    dispositions: ['Resolved', 'Escalated', 'Transferred', 'Callback Required', 'Information Provided', 'No Answer'],
    callReasons: ['General Inquiry', 'Service Request', 'Complaint Follow-up', 'Emergency', 'Technical Support', 'Other'],
    sources: ['Phone', 'IVR', 'Queue', 'Outbound', 'Transferred Call', 'External System'],
  },
  sla: {
    targetAnswerSeconds: 20,
    targetHandleSeconds: 300,
    targetHoldSeconds: 60,
    callbackOverdueMinutes: 0,
  },
  queueMapping: [
    { name: 'q_general', label: 'General', priority: 'Medium', active: true },
  ],
  ivrMenus: [
    {
      menuId: 'main',
      name: 'Main Menu',
      greeting: 'Thank you for calling. Press 1 for complaints, 2 for status inquiry, 0 for agent.',
      options: [
        { key: '1', label: 'File Complaint', action: 'queue', target: 'q_general' },
        { key: '2', label: 'Status Inquiry', action: 'queue', target: 'q_general' },
        { key: '0', label: 'Agent', action: 'queue', target: 'q_general' },
      ],
    },
  ],
  didRouting: [
    { did: '051-111-000-000', label: 'UAN Helpline', targetType: 'ivr', target: 'main', active: true },
  ],
  businessHours: {
    timezone: 'Asia/Karachi',
    weekdays: [
      { day: 'Monday',    open: true,  openTime: '09:00', closeTime: '17:00' },
      { day: 'Tuesday',   open: true,  openTime: '09:00', closeTime: '17:00' },
      { day: 'Wednesday', open: true,  openTime: '09:00', closeTime: '17:00' },
      { day: 'Thursday',  open: true,  openTime: '09:00', closeTime: '17:00' },
      { day: 'Friday',    open: true,  openTime: '09:00', closeTime: '17:00' },
      { day: 'Saturday',  open: false, openTime: '09:00', closeTime: '13:00' },
      { day: 'Sunday',    open: false, openTime: '09:00', closeTime: '13:00' },
    ],
    holidays: [],
  },
  menu: {
    title: 'Contact Center',
    route: '',
    frame: 'TELEPHONY_CONTACT_CENTER',
    order: 15,
    icon: 'headset_mic',
    subMenu: [
      {
        title: 'Agent Console',
        route: '/telephony',
        frame: 'CSR_CONSOLE',
        order: 1,
        icon: '',
      },
      {
        title: 'Social Media Comments',
        route: '/omnichannel/inbox',
        frame: 'CSR_CONSOLE',
        order: 2,
        icon: '',
      },
      {
        title: 'Telephony Messages',
        route: '/telephony/messages',
        frame: 'CSR_CONSOLE',
        order: 3,
        icon: '',
      },
      {
        title: 'Message History',
        route: '/telephony/messages/history',
        frame: 'CSR_CONSOLE',
        order: 4,
        icon: '',
      },
      {
        title: 'Call History',
        route: '/telephony/history',
        frame: 'CSR_CONSOLE',
        order: 5,
        icon: '',
      },
      {
        title: 'Extension Status',
        route: '/telephony/extensions',
        frame: 'CSR_CONSOLE',
        order: 6,
        icon: '',
      },
      {
        title: 'Control Room',
        route: '/telephony/supervisor/control-room',
        frame: 'SUPERVISOR',
        order: 7,
        icon: '',
      },
      {
        title: 'Agent Wallboard',
        route: '/telephony/supervisor/wallboard',
        frame: 'SUPERVISOR',
        order: 8,
        icon: '',
      },
      {
        title: 'Queue Monitor',
        route: '/telephony/supervisor/queue-monitor',
        frame: 'SUPERVISOR',
        order: 9,
        icon: '',
      },
      {
        title: 'IVR Voice Complaints',
        route: '/telephony/supervisor/ivr-submissions',
        frame: 'IVR_VOICE_COMPLAINTS',
        order: 10,
        icon: '',
      },
      {
        title: 'Callback Worklist',
        route: '/telephony/supervisor/callbacks',
        frame: 'SUPERVISOR',
        order: 11,
        icon: '',
      },
      {
        title: 'Operational Analytics',
        route: '/telephony/supervisor/operational-report',
        frame: 'SUPERVISOR',
        order: 12,
        icon: '',
      },
      {
        title: 'Live Chat Monitor',
        route: '/telephony/supervisor/live-chats',
        frame: 'SUPERVISOR',
        order: 13,
        icon: '',
      },
      {
        title: 'Settings',
        route: '/telephony/settings',
        frame: 'TELEPHONY_CONFIG',
        order: 14,
        icon: '',
      },
      {
        title: 'Directory Config',
        route: '/telephony/settings/directory',
        frame: 'TELEPHONY_CONFIG',
        order: 15,
        icon: '',
      },
      {
        title: 'Extension Config',
        route: '/telephony/settings/extension-config',
        frame: 'TELEPHONY_CONFIG',
        order: 16,
        icon: '',
      },
      {
        title: 'Extension Logs',
        route: '/telephony/settings/extension-logs',
        frame: 'TELEPHONY_CONFIG',
        order: 17,
        icon: '',
      },
    ],
  },
  storageKeys: {
    sipExtension: 'telephony-sip-ext',
    sipPassword: 'telephony-sip-pass',
    legacySipExtension: 'grmis-sip-ext',
    legacySipPassword: 'grmis-sip-pass',
  },
};
